import type { Data } from '@strapi/types';
import { type FunctionComponent, useState } from 'react';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { Button } from '@/ui-components/button/button';
import { SearchableDropdown } from '@/ui-components/dropdown/searchable-dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Input } from '@/ui-components/input/input';
import { Modal } from '@/ui-components/modal/modal';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

import { useSmartCardsParams } from '../hooks/use-smart-cards-params';
import { REGION_OPTIONS } from './constants';

export const CreateDocumentModal: FunctionComponent = () => {
	const { t, handleDocumentAction, error, setError } = useSmartCardsParams();
	const { getSearchParams } = useNavigation();
	const { value } = getSearchParams();
	const { documentTypes } = useAppContext();
	const documentType = documentTypes?.[Number.parseInt(value, 10)];
	const [documentName, setDocumentName] = useState(t('Untitled'));
	const [isDisabled, setIsDisabled] = useState(false);
	const [region, setRegion] = useState<Data.Entity<'api::project-document.project-document'>['region']>('IEC');

	if (!documentType) return null;

	const { title, url, isBeta } = documentType;

	if (!title || !url) return null;

	const onSubmit = () => {
		setIsDisabled(true);
		handleDocumentAction({
			documentTypeName: title,
			url,
			documentName,
			...(region ? { region } : {}),
		});
	};

	return (
		<Modal
			name='CreateDocument'
			title={{ text: t('T_CREATE_NEW_TOOL_DOCUMENT', { documentType: t(title) }), variant: 'h5' }}
			dismissible
			closeButton
		>
			<Flexbox direction='col' className='min-h-[200px] max-w-[500px] gap-3'>
				<Input
					label={t('T_PROJECT_DOCUMENT_NAME')}
					value={documentName}
					name='title'
					placeholder={t('T_ENTER_PROJECT_DOCUMENT_NAME')}
					onChange={(e) => {
						setDocumentName(e.target.value);
						setError({ message: '', details: '' });
					}}
					onKeyUp={(e) => (e.key === 'Enter' ? onSubmit() : undefined)}
					sizing='small'
					containerClassname='w-full'
					error={error.details === 'title' ? error.message : null}
				/>
				{title === 'T_CURVES' && (
					<>
						<Paragraph variant='text-base'>{t('T_REGION')}</Paragraph>
						<SearchableDropdown
							className='pr-0 '
							sizing='small'
							options={REGION_OPTIONS}
							onSelect={(value) =>
								!Array.isArray(value) && setRegion(value as Data.Entity<'api::project-document.project-document'>['region'])
							}
							placeholder={t('T_SELECT_REGION')}
							isSearchable={false}
							externalSearchTerm={region ?? ''}
							onClear={() => setRegion(undefined)}
							inputBackground='light'
						/>
					</>
				)}
				{isBeta && (
					<Flexbox direction='col' className='bg-n100_grey rounded-lg gap-1 p-2 dark:bg-bg_dark'>
						<Paragraph variant='text-sm-semibold' className='dark:text-n700_text_dark_mode'>
							{t('T_BETA_NOTE_TITLE')}
						</Paragraph>
						<Paragraph variant='text-xs' className='dark:text-n700_text_dark_mode'>
							{t('T_BETA_NOTE_TEXT')}
						</Paragraph>
					</Flexbox>
				)}
				<Flexbox justify='end' className='mt-10 [&>*]:w-32'>
					<Button onClick={onSubmit} variant='primary' sizing='small' disabled={!documentName.trim() || !region || isDisabled}>
						{t('T_CREATE')}
					</Button>
				</Flexbox>
			</Flexbox>
		</Modal>
	);
};
